/**
 * «A quién», el primer paso del asistente: cuatro opciones con los nombres del comerciante (29/10/2026), cada una con
 * cuándo conviene: «Todos tus clientes» (el Público de Activa), «Una lista guardada» (un público de la base), «Un
 * archivo» (el Excel) y «Los grupos de una segmentación» (una campaña de Activa). Debajo de lo elegido, «Por compras» y
 * «Excluir» plegados. Antes de pasar a una segmentación con un filtro por compras armado, se avisa que ahí no se usa. La
 * de un seguimiento la fija la original: sólo se dice cuál es (`AudienciaSeguimiento`).
 *
 * La lista guardada y el archivo tienen sus propias pantallas (crear un público, cargar contactos) que vuelven acá con
 * `?publico=`: sin borrador todavía, a `campanas/nueva`, que lo recibe igual (no hace falta crear un borrador para ir).
 *
 * Lo elegido sale del borrador (`useBorradorCampana`) y la opción, las listas para elegir y las cuentas, de
 * `useAudienciaCampana`.
 */
import { AlertTriangle } from "lucide-react";
import type { ResumenCompras, ResumenExclusiones } from "../../types";
import type { BorradorCampana } from "../../hooks/asistente/useBorradorCampana";
import type { AudienciaCampana } from "../../hooks/asistente/useAudienciaCampana";
import { AudienciaBase } from "../AudienciaBase";
import { AudienciaExcel } from "../AudienciaExcel";
import { AudienciaPublico, FILTRO_PUBLICO_VACIO } from "../AudienciaPublico";
import { FiltroComprasBloque } from "../FiltroComprasBloque";
import { ExcluirBloque } from "../ExcluirBloque";
import { AudienciaSeguimiento } from "../SeguimientoCampana";
import { PasoAudiencia } from "./PasoAudiencia";
import type { Pestana } from "../../logic/asistente";
import { NOMBRE_AUDIENCIA } from "../../logic/vasAMandar";

const B = "/activa/oficial";

/** Las opciones de «A quién», en el orden en que se muestran, con cuándo conviene cada una (el nombre, `NOMBRE_AUDIENCIA`). */
const OPCIONES: { valor: Pestana; cuando: string }[] = [
  { valor: "publico", cuando: "Para hablarle a todo tu Público, o a un segmento." },
  { valor: "base", cuando: "Si ya armaste un público en tu base de contactos." },
  { valor: "excel", cuando: "Si tenés la lista en un Excel o un CSV." },
  { valor: "activa", cuando: "Para seguir una campaña de Activa, con sus grupos." },
];

export function ElegirAudiencia({ borrador, audiencia }: { borrador: BorradorCampana; audiencia: AudienciaCampana }) {
  const {
    campana, idCampana, audiencias, sinWhatsapp, errorAudiencias, publicos, setPublicos, errorPublicos, cargarPublicos,
    audienciaId, setAudienciaId, modoGrupos, setModoGrupos, elegidos, setElegidos, publicoId, setPublicoId,
    filtroPublico, setFiltroPublico, filtroCompras, setFiltroCompras, excluir, setExcluir,
  } = borrador;
  const {
    tab, elegirTab, tabPendiente, setTabPendiente, listaSubida, setListaSubida, opcionesCompras, errorOpcionesCompras, reintentarCompras,
    comprasParaContar, opcionesExcluir, errorOpcionesExcluir, reintentarExcluir, excluirParaContarAhora, hayListasSiempre,
    alcanceAud, puedenDelGrupo, puedenElegidos, prepararDeNuevo, setContadoPublico, setContadoBase, guardadaYaNoEsta,
  } = audiencia;

  if (campana?.seguimientoDe) return <AudienciaSeguimiento seguimientoDe={campana.seguimientoDe} />;
  // la vuelta de crear un público o de cargar contactos: el borrador, o `campanas/nueva` si todavía no hay
  const volverA = idCampana ? `${B}/campanas/${encodeURIComponent(idCampana)}/editar?paso=audiencia` : `${B}/campanas/nueva?paso=audiencia`;

  const bloqueExcluir = (r: { exclusiones: ResumenExclusiones | null; contando: boolean }) => (
    <ExcluirBloque
      excluir={excluir}
      onExcluir={setExcluir}
      campanas={opcionesExcluir?.campanas ?? null}
      listas={opcionesExcluir?.listas ?? null}
      errorOpciones={errorOpcionesExcluir}
      onReintentar={reintentarExcluir}
      guardadas={campana?.audiencia?.excluir}
      resumen={r.exclusiones}
      contando={r.contando}
      campanaId={campana?.id}
    />
  );
  const bloqueCompras = (r: { resumen: ResumenCompras | null; exclusiones: ResumenExclusiones | null; contando: boolean }) => (
    <>
      <FiltroComprasBloque
        filtro={filtroCompras}
        onFiltro={setFiltroCompras}
        opciones={opcionesCompras}
        errorOpciones={errorOpcionesCompras}
        onReintentar={reintentarCompras}
        resumen={r.resumen}
        contando={r.contando}
      />
      {bloqueExcluir(r)}
    </>
  );

  return (
    <div className="space-y-5">
      {tabPendiente === "activa" && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert" data-testid="aviso-cambio-pestana">
          <p className="flex items-start gap-2">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            <span>
              Una segmentación ya sale de las compras: el filtro «Por compras» que armaste no se usa con ella. Las exclusiones sí
              se conservan. Si después volvés a «Todos tus clientes» o a «Una lista guardada», el filtro sigue acá.
            </span>
          </p>
          <span className="flex shrink-0 items-center gap-3">
            <button type="button" onClick={() => elegirTab("activa", true)} className="font-semibold underline">Cambiar igual</button>
            <button type="button" onClick={() => setTabPendiente(null)} className="font-semibold text-amber-800 hover:underline">Quedarme acá</button>
          </span>
        </div>
      )}
      <div role="radiogroup" aria-label="A quién le mandás" className="grid gap-2 sm:grid-cols-2">
        {OPCIONES.map((o) => (
          <button key={o.valor} type="button" role="radio" aria-checked={tab === o.valor} onClick={() => elegirTab(o.valor)}
            className={`min-h-[44px] rounded-xl border px-3 py-2.5 text-left ${tab === o.valor ? "border-violet-400 bg-violet-50/60" : "border-slate-200 hover:border-slate-300"}`}>
            <span className="block text-sm font-semibold text-slate-900">{NOMBRE_AUDIENCIA[o.valor]}</span>
            <span className="block text-xs text-slate-500">{o.cuando}</span>
          </button>
        ))}
      </div>
      {/* la guardada ya no está (una lista borrada, una segmentación que ya no se ofrece): no aparece marcada, y se dice */}
      {guardadaYaNoEsta && tab === guardadaYaNoEsta.tipo && (
        <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert" data-testid="audiencia-guardada-no-esta">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" /> {guardadaYaNoEsta.texto}
        </p>
      )}
      {tab === null ? (
        <div className="h-32 rounded-xl bg-slate-100 animate-pulse" aria-busy="true" />
      ) : tab === "publico" ? (
        <div>
          <AudienciaPublico filtro={filtroPublico} onFiltro={setFiltroPublico} lista={listaSubida} compras={comprasParaContar} excluir={excluirParaContarAhora} porCompras={bloqueCompras}
            onContado={setContadoPublico} />
        </div>
      ) : tab === "base" ? (
        <div>
          <AudienciaBase
            publicos={publicos}
            error={errorPublicos}
            onReintentar={() => { setPublicos(null); void cargarPublicos(); }}
            publicoId={publicoId}
            onElegir={(p) => setPublicoId(p.id)}
            volverA={volverA}
            compras={comprasParaContar}
            excluir={excluirParaContarAhora}
            listasSiempre={hayListasSiempre}
            porCompras={bloqueCompras}
            onContado={setContadoBase}
          />
        </div>
      ) : tab === "excel" ? (
        <div>
          <AudienciaExcel
            volverA={volverA}
            // la lista cruzada con el Público: la campaña sale a «Todos tus clientes», sólo a los del archivo
            onUsarLista={(l) => { setListaSubida(l); setFiltroPublico({ ...FILTRO_PUBLICO_VACIO, lista: l.id }); elegirTab("publico"); }}
          />
        </div>
      ) : (
        <div>
          <PasoAudiencia
            audiencias={audiencias}
            sinWhatsapp={sinWhatsapp}
            error={errorAudiencias}
            audienciaId={audienciaId}
            onAudiencia={(v) => { setAudienciaId(v); setElegidos([]); }}
            modoGrupos={modoGrupos}
            onModoGrupos={setModoGrupos}
            elegidos={elegidos}
            onElegidos={setElegidos}
            alcance={alcanceAud}
            puedenDelGrupo={puedenDelGrupo}
            puedenElegidos={puedenElegidos}
            onPrepararDeNuevo={prepararDeNuevo}
          />
          {/* las exclusiones también con un análisis de Activa (29/10/2026); el filtro por compras no (ya sale de ellas) */}
          {audienciaId && <div className="mt-5">{bloqueExcluir({ exclusiones: alcanceAud.alcance?.exclusiones ?? null, contando: alcanceAud.cargando })}</div>}
        </div>
      )}
    </div>
  );
}
